import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Camera, ImagePlus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace, Button } from '@/components/Button';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import { DateField, TextField } from '@/components/Field';
import { Segmentado } from '@/components/Segmentado';
import { nuevoId } from '@/db/ids';
import {
  borrarFoto,
  corregirFoto,
  fotosSinBytes,
  guardarFoto,
  leerBytesDeFoto,
  leerFotos,
} from '@/db/fotos';
import { mensajeDeError } from '@/db/tx';
import type { Photo } from '@/db/types';
import type { FechaLocal } from '@/domain/dates';
import {
  NOMBRE_POSE,
  NOTA_MAX,
  POSES,
  porDia,
  textoDelDia,
  textoDelEspacio,
  type Pose,
} from '@/domain/fotos';
import { fechaCorta, fechaDelDia } from '@/domain/hoy';
import { formatearBytes } from '@/lib/storage';
import { reducirImagen } from '@/lib/imagen';
import { useFechaHoy } from '@/lib/useFechaHoy';

/*
 * Fotos de progreso (Fase 6, 0.14.0; Req. 6.17; design.md 7.10).
 *
 * Las fotos se quedan en el teléfono: no salen a ningún servidor. Para que no
 * se pierdan hay que respaldarlas, y eso se hace en Respaldos → "Respaldo con
 * fotos", que entrega un ZIP; el respaldo de siempre, en JSON, no las lleva.
 * Esa distinción se dice en pantalla, no se da por entendida.
 */

const VOLVER_A_CUERPO = { a: '/cuerpo', texto: 'Cuerpo', etiqueta: 'Volver a Cuerpo' };
const VOLVER_A_FOTOS = { a: '/cuerpo/fotos', texto: 'Fotos', etiqueta: 'Volver a tus fotos' };

const OPCIONES_POSE = POSES.map((p) => ({ valor: p, etiqueta: NOMBRE_POSE[p] }));
const minuscula = (t: string) => t.toLocaleLowerCase('es-MX');

/**
 * La dirección temporal con que el navegador muestra un Blob guardado. Se
 * libera al cambiar o al salir: sin eso, cada vez que se abre la galería
 * quedarían fotos retenidas en memoria.
 */
function useUrlDeFoto(id: string | null): string | null {
  const blob = useLiveQuery(() => (id === null ? Promise.resolve(undefined) : leerBytesDeFoto(id)), [id]);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (blob === undefined) {
      setUrl(null);
      return;
    }
    const creada = URL.createObjectURL(blob);
    setUrl(creada);
    return () => URL.revokeObjectURL(creada);
  }, [blob]);

  return url;
}

function Imagen({ foto, className }: { foto: Photo; className: string }) {
  const url = useUrlDeFoto(foto.id);
  const descripcion = `${NOMBRE_POSE[foto.pose]}, ${minuscula(fechaCorta(foto.fecha))}`;

  if (url === null) {
    return (
      <div
        className={`${className} grid place-items-center bg-superficie-suave text-[12px] text-texto-suave`}
        role="img"
        aria-label={`${descripcion}: la imagen no está`}
      >
        Sin imagen
      </div>
    );
  }
  return <img src={url} alt={descripcion} className={className} />;
}

// ---------------------------------------------------------------------------
// La galería, por día y la más reciente primero
// ---------------------------------------------------------------------------

export function FotosPage() {
  const fotos = useLiveQuery(() => leerFotos(), []);
  const sinBytes = useLiveQuery(() => fotosSinBytes(), []);
  if (fotos === undefined || sinBytes === undefined) return null;

  const dias = porDia(fotos);

  return (
    <div>
      <PageHeader
        titulo="Tus fotos."
        descripcion="El espejo cambia antes que la báscula. Toma la foto en el mismo lugar y con la misma luz para poder comparar."
        volver={VOLVER_A_CUERPO}
      />

      {sinBytes.length > 0 && (
        <div className="mb-6">
          <Banner
            tono="aviso"
            titulo={
              sinBytes.length === 1
                ? '1 foto se quedó sin su imagen.'
                : `${sinBytes.length} fotos se quedaron sin su imagen.`
            }
          >
            {`Su fecha y su nota siguen aquí, pero la imagen no está (pudo pasar si el iPhone se
            quedó sin espacio al guardarla). ${
              sinBytes.length === 1 ? 'Bórrala y tómala' : 'Bórralas y tómalas'
            } de nuevo.`}
          </Banner>
        </div>
      )}

      {fotos.length === 0 ? (
        <EstadoVacio
          icono={Camera}
          titulo="Tu primera foto"
          descripcion="Las fotos se guardan solo en este iPhone y nunca salen a internet. Para que no se pierdan, inclúyelas en tu respaldo: en Respaldos hay un «Respaldo con fotos»."
        >
          <BotonEnlace to="/cuerpo/fotos/nueva" icono={ImagePlus}>
            Agregar una foto
          </BotonEnlace>
        </EstadoVacio>
      ) : (
        <>
          <BotonEnlace to="/cuerpo/fotos/nueva" icono={ImagePlus}>
            Agregar una foto
          </BotonEnlace>

          <div className="mt-6 space-y-6">
            {dias.map((dia) => (
              <Seccion key={dia.fecha} titulo={fechaCorta(dia.fecha)} extra={textoDelDia(dia.fotos)}>
                <ul className="grid grid-cols-3 gap-2">
                  {dia.fotos.map((foto) => (
                    <li key={foto.id}>
                      <Link
                        to={`/cuerpo/foto/${foto.id}`}
                        className="block overflow-hidden rounded-[15px] bg-superficie active:opacity-70"
                      >
                        <Imagen foto={foto} className="aspect-[3/4] w-full object-cover" />
                        <span className="block py-2 text-center text-[12px] text-texto-suave">
                          {NOMBRE_POSE[foto.pose]}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Seccion>
            ))}
          </div>

          <div className="mt-8">
            <Card>
              <span className="antetitulo">En este iPhone</span>
              <p className="mt-2 text-[17px] leading-snug text-texto">{textoDelEspacio(fotos)}</p>
              <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
                Tus fotos no salen de aquí: no hay servidor. El respaldo de siempre, en JSON, no las
                lleva. Para incluirlas usa Respaldos → «Respaldo con fotos», que entrega un ZIP.
              </p>
              <div className="mt-4">
                <BotonEnlace to="/mas/respaldos" variante="secundario">
                  Ir a Respaldos
                </BotonEnlace>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agregar una foto
// ---------------------------------------------------------------------------

export function FotoNuevaPage() {
  const hoy = useFechaHoy();
  const navegar = useNavigate();
  // El id se crea al abrir: dos toques de "Guardar" no duplican (Req. 1.9)
  const [id] = useState(() => nuevoId('fot'));
  const [fecha, setFecha] = useState<FechaLocal>(hoy);
  const [pose, setPose] = useState<Pose>('frente');
  const [nota, setNota] = useState('');
  const [elegida, setElegida] = useState<{ datos: Blob; url: string } | null>(null);
  const [preparando, setPreparando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  // La vista previa se libera al cambiar de foto o al salir de la pantalla
  useEffect(() => {
    const url = elegida?.url;
    return () => {
      if (url !== undefined) URL.revokeObjectURL(url);
    };
  }, [elegida]);

  async function elegir(archivo: File) {
    setError(null);
    setPreparando(true);
    try {
      const datos = await reducirImagen(archivo);
      setElegida({ datos, url: URL.createObjectURL(datos) });
    } catch (e) {
      setElegida(null);
      setError(mensajeDeError(e));
    } finally {
      setPreparando(false);
      // Así se puede volver a elegir el mismo archivo si hizo falta reintentar
      if (entrada.current !== null) entrada.current.value = '';
    }
  }

  async function guardar() {
    if (guardando || elegida === null) return;
    setError(null);
    setGuardando(true);
    try {
      await guardarFoto({ id, fecha, pose, nota, datos: elegida.datos }, hoy);
      void navegar('/cuerpo/fotos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        antetitulo={fechaDelDia(fecha)}
        titulo="Agregar una foto."
        descripcion="Se guarda más chica, sin el lugar donde se tomó y solo en este iPhone."
        volver={VOLVER_A_FOTOS}
      />

      <input
        ref={entrada}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          if (archivo !== undefined) void elegir(archivo);
        }}
      />

      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void guardar();
        }}
      >
        {elegida === null ? (
          <Card>
            <p className="text-[13px] leading-relaxed text-texto-suave">
              Toma la foto con la cámara o elige una del carrete. Se reduce a 1,280 px del lado
              mayor para que quepa en tus respaldos.
            </p>
            <div className="mt-4">
              <Button
                variante="secundario"
                disabled={preparando}
                icono={Camera}
                onClick={() => entrada.current?.click()}
              >
                {preparando ? 'Preparando la foto…' : 'Tomar o elegir una foto'}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            <img
              src={elegida.url}
              alt="La foto que vas a guardar"
              className="w-full rounded-panel bg-superficie-suave object-contain"
            />
            <p className="text-[13px] text-texto-suave">
              Quedará en {formatearBytes(elegida.datos.size)}.
            </p>
            <Button
              variante="fantasma"
              disabled={preparando}
              onClick={() => entrada.current?.click()}
            >
              {preparando ? 'Preparando la foto…' : 'Elegir otra'}
            </Button>
          </div>
        )}

        <DateField
          etiqueta="Fecha"
          valor={fecha}
          onCambio={(v) => {
            setFecha(v);
            setError(null);
          }}
          max={hoy}
        />
        <Segmentado
          etiqueta="Pose"
          etiquetaVisible
          completo
          valor={pose}
          opciones={OPCIONES_POSE}
          onCambio={setPose}
        />
        <TextField
          etiqueta="Nota (opcional)"
          valor={nota}
          onCambio={setNota}
          maxLength={NOTA_MAX}
          placeholder="Por ejemplo: con la luz de la ventana"
        />

        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}

        <Button type="submit" disabled={guardando || elegida === null}>
          {guardando ? 'Guardando…' : 'Guardar foto'}
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ver una foto, corregir sus datos o borrarla
// ---------------------------------------------------------------------------

export function FotoPage() {
  const { id = '' } = useParams();
  const hoy = useFechaHoy();
  const fotos = useLiveQuery(() => leerFotos(), []);
  if (fotos === undefined) return null;
  const foto = fotos.find((f) => f.id === id);
  if (foto === undefined) {
    return (
      <div>
        <PageHeader titulo="No está." volver={VOLVER_A_FOTOS} />
        <EstadoVacio titulo="Esa foto ya no existe." descripcion="Puede que se haya borrado." />
      </div>
    );
  }
  return <FormularioFoto key={foto.id} foto={foto} hoy={hoy} />;
}

function FormularioFoto({ foto, hoy }: { foto: Photo; hoy: FechaLocal }) {
  const navegar = useNavigate();
  const notaInicial = foto.nota ?? '';
  const [fecha, setFecha] = useState<FechaLocal>(foto.fecha);
  const [pose, setPose] = useState<Pose>(foto.pose);
  const [nota, setNota] = useState(notaInicial);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);

  async function guardar() {
    if (guardando) return;
    setError(null);
    // Sin cambios no se escribe nada: queda exactamente como estaba
    if (fecha === foto.fecha && pose === foto.pose && nota.trim() === notaInicial.trim()) {
      void navegar('/cuerpo/fotos', { replace: true });
      return;
    }
    setGuardando(true);
    try {
      await corregirFoto(foto.id, { fecha, pose, nota }, hoy);
      void navegar('/cuerpo/fotos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function borrar() {
    setGuardando(true);
    try {
      await borrarFoto(foto.id);
      void navegar('/cuerpo/fotos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        antetitulo={fechaDelDia(foto.fecha)}
        titulo={`${NOMBRE_POSE[foto.pose]}.`}
        descripcion="Puedes corregir la fecha, la pose y la nota. La imagen no se edita nunca."
        volver={VOLVER_A_FOTOS}
      />

      <Imagen foto={foto} className="w-full rounded-panel bg-superficie-suave object-contain" />
      <p className="mt-2 text-[13px] text-texto-suave">{formatearBytes(foto.bytes)}</p>

      <form
        className="mt-5 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void guardar();
        }}
      >
        <DateField
          etiqueta="Fecha"
          valor={fecha}
          onCambio={(v) => {
            setFecha(v);
            setError(null);
          }}
          max={hoy}
        />
        <Segmentado
          etiqueta="Pose"
          etiquetaVisible
          completo
          valor={pose}
          opciones={OPCIONES_POSE}
          onCambio={setPose}
        />
        <TextField etiqueta="Nota (opcional)" valor={nota} onCambio={setNota} maxLength={NOTA_MAX} />

        {error !== null && (
          <Banner tono="alerta" titulo="No se pudo">
            {error}
          </Banner>
        )}

        <Button type="submit" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </form>

      <div className="mt-6 space-y-2">
        {confirmarBorrar ? (
          <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
            <p className="text-[13px] text-texto">
              ¿Borrar esta foto? Se va con su imagen y no se puede deshacer; las demás no cambian.
              Si está en un respaldo con fotos, ahí sigue.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button variante="peligro" disabled={guardando} onClick={() => void borrar()}>
                Sí, borrar
              </Button>
              <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
            Borrar foto
          </Button>
        )}
      </div>
    </div>
  );
}
